import { isIOS } from '@/core/utils/deviceUtils';

type OpenWebViewLinkOptions = {
  event?: { preventDefault(): void };
  isApp?: boolean;
};

export type StoreBridgeData = {
  link: string;
};

export type StoreBridgePayload = {
  storeBridgeData: StoreBridgeData;
};

function isNativeSchemeLink(link: string): boolean {
  const trimmed = link.trim().toLowerCase();
  return trimmed.startsWith('tel:') || trimmed.startsWith('mailto:');
}

function isAbsoluteHttpLink(link: string): boolean {
  const trimmed = link.trim().toLowerCase();
  return trimmed.startsWith('http://') || trimmed.startsWith('https://');
}

const STORE_LOCATOR_HTML = 'store.html';

export function isStoreLocatorPage(): boolean {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname;
  return pathname === `/${STORE_LOCATOR_HTML}` || pathname.endsWith(`/${STORE_LOCATOR_HTML}`);
}

export function buildStoreBridgePayload(link: string): StoreBridgePayload {
  const appBridgeData: StoreBridgeData = { link };
  return {
    storeBridgeData: { ...appBridgeData },
  };
}

export function serializeStoreBridgePayload(link: string): string {
  return JSON.stringify(buildStoreBridgePayload(link));
}

export function isStoreLocatorLink(link: string): boolean {
  const path = link.split('?')[0]?.split('#')[0] ?? link;
  return path.endsWith(`/${STORE_LOCATOR_HTML}`) || path.endsWith(STORE_LOCATOR_HTML);
}

/**
 * Resolve a relative link to an absolute URL for the native app bridge.
 */
export function resolveAppLink(link: string): string {
  const trimmed = link.trim();
  if (!trimmed) return trimmed;

  if (isNativeSchemeLink(trimmed) || isAbsoluteHttpLink(trimmed)) {
    return trimmed;
  }

  if (typeof window === 'undefined') {
    return trimmed;
  }

  return trimmed.startsWith('/')
    ? `${window.location.origin}${trimmed}`
    : `${window.location.origin}/${trimmed}`;
}

function postToAppBridge(appLink: string): void {
  if (typeof window === 'undefined') return;

  const useStoreBridgePayload = isStoreLocatorPage();
  const bridgePayload = useStoreBridgePayload ? serializeStoreBridgePayload(appLink) : appLink;

  if (isIOS()) {
    if (window.setDataForIOS) {
      window.setDataForIOS(appLink);
      return;
    }
  } else if (window.AndroidBridge?.setData) {
    window.AndroidBridge.setData(bridgePayload);
    return;
  }

  window.location.href = appLink;
}

/**
 * Open a link from web or app.
 * In app webview, outbound links use the native bridge except store.html itself.
 */
export function openWebViewLink(link: string, options?: OpenWebViewLinkOptions): void {
  if (!link.trim()) return;

  if (isNativeSchemeLink(link)) {
    return;
  }

  options?.event?.preventDefault();

  const isApp = options?.isApp ?? false;

  if (!isApp || isStoreLocatorLink(link)) {
    if (typeof window !== 'undefined') {
      window.location.href = link;
    }
    return;
  }

  postToAppBridge(resolveAppLink(link));
}
